import { useCallback, useEffect, useMemo, useState } from "react";
import { useNotify } from "react-admin";
import { handleAxiosError } from "../../../../common/data/axiosErrorHandler";
import {
  HosDetailsDto,
  HosLogsApiFactory,
} from "../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { useFleetManagerHubHosUpdates } from "../../../../common/hooks/useFleetManagerHubHosUpdates";
import { HosIndicator } from "../../common/HosIndicator";
import { getHosTimers } from "../../hosUtils";
import { HosErrorIndicator } from "./HosErrorIndicator";
import { HosPendingIndicator } from "./HosPendingIndicator";

export const HOS_POLL_INTERVAL_MS = 15000;
export const HOS_MAX_POLL_ATTEMPTS = 20;

const defaultHosDetails: HosDetailsDto = {
  maxCycle: 8 * 24 * 60,
  maxDrive: 11 * 60,
  maxShift: 14 * 60,
};

interface Props {
  hosDetails?: HosDetailsDto;
  driverId?: number;
  onHosDataRefresh: () => Promise<void>;
}

export const DriverLogHeaderHosInfo = ({
  hosDetails,
  driverId,
  onHosDataRefresh,
}: Props) => {
  const axios = useAxiosInstance();
  const [time, setTime] = useState<Date>(new Date());
  const [rebuildInFlight, setRebuildInFlight] = useState(false);
  const [awaitingServerState, setAwaitingServerState] = useState(false);
  const notify = useNotify();
  const data = hosDetails ?? defaultHosDetails;
  const isPending = hosDetails?.isPending === true;
  const isError = hosDetails?.isError === true;
  const showPending = isPending && !isError;

  useFleetManagerHubHosUpdates(driverId, onHosDataRefresh);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!awaitingServerState) {
      return;
    }

    if (showPending || (!isPending && !isError)) {
      setAwaitingServerState(false);
    }
  }, [awaitingServerState, isError, isPending, showPending]);

  useEffect(() => {
    if (!showPending && !awaitingServerState) {
      return;
    }

    let cancelled = false;
    let attempts = 0;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      attempts += 1;
      await onHosDataRefresh();

      if (cancelled) {
        return;
      }

      if (attempts >= HOS_MAX_POLL_ATTEMPTS) {
        setAwaitingServerState(false);
        return;
      }

      timeout = setTimeout(poll, HOS_POLL_INTERVAL_MS);
    };

    timeout = setTimeout(poll, HOS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [awaitingServerState, driverId, onHosDataRefresh, showPending]);

  const handleHosInfoRefresh = useCallback(async () => {
    if (
      !axios ||
      !driverId ||
      !isError ||
      rebuildInFlight ||
      awaitingServerState
    ) {
      return;
    }

    setRebuildInFlight(true);
    try {
      await HosLogsApiFactory(
        undefined,
        axios.defaults.baseURL,
        axios
      ).apiAppHoslogsRebuildHosInfoPost(driverId);
      setAwaitingServerState(true);
      await onHosDataRefresh();
    } catch (e) {
      console.error(handleAxiosError(e));
      notify(handleAxiosError(e), { type: "error" });
    } finally {
      setRebuildInFlight(false);
    }
  }, [
    axios,
    awaitingServerState,
    driverId,
    isError,
    notify,
    onHosDataRefresh,
    rebuildInFlight,
  ]);

  const {
    cycleWork,
    shiftDrive,
    shiftWork,
    regularShiftDrive,
    regularShiftWork,
  } = useMemo(() => getHosTimers(data, time), [data, time]);

  if (!hosDetails) {
    return null;
  }

  return (
    <div id="driver-log-header-hos-info">
      {showPending ? <HosPendingIndicator /> : null}
      {isError ? (
        <HosErrorIndicator
          onRefresh={handleHosInfoRefresh}
          refreshDisabled={rebuildInFlight || awaitingServerState}
        />
      ) : null}
      <HosIndicator
        label="Drive"
        valueAsMinutes={regularShiftDrive}
        maxValueAsMinutes={data.maxDrive}
        floater={
          <HosIndicator
            label="SSB"
            valueAsMinutes={shiftDrive}
            maxValueAsMinutes={data.maxDrive}
          />
        }
      />
      <HosIndicator
        label="Shift"
        valueAsMinutes={regularShiftWork}
        maxValueAsMinutes={data.maxShift || 840}
        floater={
          <HosIndicator
            label="SSB"
            valueAsMinutes={shiftWork}
            maxValueAsMinutes={data.maxShift || 840}
          />
        }
      />
      <HosIndicator
        valueAsMinutes={cycleWork}
        maxValueAsMinutes={data.maxCycle || 4200}
        label="Cycle"
      />
    </div>
  );
};
